import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyApi } from '@l2u/ui/lib/api.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { CommentThread } from '@l2u/ui/components/CommentThread.tsx';
import { ReplyComposer, type ReplyPayload } from '@l2u/ui/components/ReplyComposer.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import {
  ReplyRemoveToggle,
  WillBeRemovedNote,
} from '@l2u/ui/components/ReplyRemoveToggle.tsx';
import { replyRemoveAvailable, toggleRemovalId } from '@l2u/ui/lib/replyRemoval.ts';
import {
  replyAttachmentCanPromote,
  replyAttachmentOffersExpiry,
} from '@l2u/shared/replyAttachment';
import { hasExpiredLiveFile } from '@l2u/ui/lib/documentVersions.ts';
import type { AdoptionHistoryItem, FileHistoryItem } from '@l2u/ui/lib/fileHistory.ts';
import { vendorCanReply } from '@l2u/ui/lib/documentsTab.ts';
import { questionOwesFileWithoutFiles } from '@l2u/ui/lib/visibility.ts';
import { complianceDocName } from '@l2u/ui/lib/complianceDocName.ts';
import { formatDateOnly } from '@l2u/ui/lib/expiry.ts';
import type { Question, Submission, SubmissionComment } from '@l2u/ui/lib/types.ts';
import { meKeys } from '../lib/meQueryKeys.ts';
import { postVendorReply } from '../lib/vendorReply.ts';
import { UploadStateChip } from './UploadStateChip.tsx';

const API_PREFIX = '/api/me';

/**
 * One Documents-tab document's conversation with the reviewer: its current
 * file(s), the current conversation (since the latest approval), and the reply composer when the API reply guard
 * would accept a reply (vendorCanReply mirrors it), else the closed note.
 */
export function DocumentThreadModal({
  submission,
  question,
  comments,
  onClose,
}: {
  submission: Submission;
  question: Question;
  /** This submission's current conversation, from meKeys.commentThreads(). */
  comments: SubmissionComment[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  // Same key and response shape as MyDocuments' DocumentHistoryModal, so the
  // two share one cache entry.
  const historyQuery = useQuery({
    queryKey: meKeys.filesHistory(submission.id),
    queryFn: () =>
      companyApi.get<{ files: FileHistoryItem[]; adoptions: AdoptionHistoryItem[] }>(
        `${API_PREFIX}/submissions/${submission.id}/files/history`,
      ),
  });
  const liveFiles = (historyQuery.data?.files ?? []).filter((f) => f.superseded_at === null);
  const now = new Date();

  const canReply = vendorCanReply(submission, {
    hasAdminComment: comments.some((c) => c.author_type === 'admin'),
    hasExpiredLive: hasExpiredLiveFile(liveFiles, now),
    inDocumentsTab: true,
  });

  // Current files the vendor toggled Remove on (the rows below); sent with the
  // next reply, cleared once it is sent.
  const [removals, setRemovals] = useState<string[]>([]);

  const replyMutation = useMutation({
    mutationFn: (payload: ReplyPayload) =>
      postVendorReply({
        apiPrefix: API_PREFIX,
        questionId: question.id,
        submissionId: submission.id,
        payload,
      }),
    // The same set the questionnaire reply invalidates (Questionnaire.tsx
    // postCommentMutation). The modal stays open: the thread re-renders from
    // the refreshed cache, so the vendor sees their message land.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: meKeys.commentThreads() });
      void qc.invalidateQueries({ queryKey: meKeys.fileHistories() });
      // Nested: also refreshes meKeys.filesHistory(submission.id) above.
      void qc.invalidateQueries({ queryKey: meKeys.submissions() });
      void qc.invalidateQueries({ queryKey: meKeys.all });
    },
  });

  const requiresFile = questionOwesFileWithoutFiles(question, submission);
  // Same gate as the composer: the toggles show only while a reply can carry them.
  const showRemove =
    historyQuery.isSuccess &&
    canReply &&
    replyRemoveAvailable({
      role: 'vendor',
      canPromote: replyAttachmentCanPromote(question),
      allowMultipleFiles: question.allow_multiple_files,
      requiresFile,
      hasCurrentDocs: liveFiles.length > 0,
    });

  return (
    <Modal
      open
      onClose={onClose}
      title={complianceDocName(question)}
      size="lg"
      // The shared Modal has no ✕; without a footer Escape was the only exit.
      footer={
        <Btn variant="secondary" size="sm" onClick={onClose}>
          Close
        </Btn>
      }
    >
      <p className="-mt-2 mb-4 text-sm text-slate-500">Conversation with your reviewer</p>

      <section className="mb-5">
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Current file{liveFiles.length === 1 ? '' : 's'}
        </h3>
        {historyQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : historyQuery.isError ? (
          <ErrorMessage error={historyQuery.error} />
        ) : liveFiles.length === 0 ? (
          <p className="text-sm text-slate-500">No current file.</p>
        ) : (
          <ul className="space-y-2">
            {liveFiles.map((f) => {
              const removing = showRemove && removals.includes(f.id);
              return (
                <li
                  key={f.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                    removing ? 'border-red-200 bg-red-50/60' : 'border-slate-200'
                  }`}
                >
                  <div className="min-w-0">
                    <div
                      className={`truncate text-sm font-semibold ${removing ? 'line-through opacity-60' : ''}`}
                      style={{ color: 'var(--brand-primary)' }}
                    >
                      {f.file_name}
                    </div>
                    {removing ? (
                      <WillBeRemovedNote />
                    ) : (
                      <div className="text-xs text-slate-500">
                        {f.expires_at ? `Expires ${formatDateOnly(f.expires_at)}` : 'No expiry'}
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <UploadStateChip expiresAt={f.expires_at} now={now} />
                    {showRemove && (
                      <ReplyRemoveToggle
                        fileName={f.file_name}
                        removing={removing}
                        onToggle={() => setRemovals((r) => toggleRemovalId(r, f.id))}
                        disabled={replyMutation.isPending}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <CommentThread comments={comments} />

      {/* Gated on the history having loaded: canReply reads the live files
          (an expired one opens an approved document's renewal), and the
          composer needs them as replacement targets — without them it would
          default an attachment to "New" instead of "Replaces". */}
      {historyQuery.isSuccess && (
        <div className="mt-5 border-t border-slate-100 pt-4">
          {canReply ? (
            <>
              <p className="mb-2 text-xs text-slate-500">
                Reply here. To send a renewed document, attach it as a replacement and your reviewer
                will check it.
              </p>
              <ReplyComposer
                role="vendor"
                canPromote={replyAttachmentCanPromote(question)}
                offersExpiry={replyAttachmentOffersExpiry(question)}
                allowMultipleFiles={question.allow_multiple_files}
                currentFiles={liveFiles}
                onReply={(p) => replyMutation.mutate(p)}
                pending={replyMutation.isPending}
                error={replyMutation.error}
                requiresExpiry={question.requires_expiry}
                requiresFile={requiresFile}
                removals={removals}
                onClearRemovals={() => setRemovals([])}
              />
            </>
          ) : (
            <p className="text-sm text-slate-500">
              This conversation is closed. It reopens when your reviewer leaves a new comment.
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
